import React, { useState, useEffect } from "react";
import "./TrackingForm.css";
import { getBrandConfig, BrandConfig } from "../config/branding";

interface TrackingEvent {
  time_iso: string;
  time_utc: string;
  description: string;
  location?: string;
  stage?: string;
}

interface TrackingResult {
  number: string;
  track_info?: {
    latest_status?: {
      status: string;
      sub_status?: string;
    };
    tracking?: {
      providers: Array<{
        provider: {
          name: string;
          key?: string;
        };
        events?: TrackingEvent[];
      }>;
    };
  };
}

const TrackingForm: React.FC = () => {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [brandConfig, setBrandConfig] = useState<BrandConfig>(getBrandConfig(null));
  const [storeLoading, setStoreLoading] = useState(true);

  // Calculate real calendar days between two dates
  const calculateRealDays = (startDateIso: string, endDateIso: string): number => {
    try {
      const startDate = new Date(startDateIso);
      const endDate = new Date(endDateIso);
      
      // Reset time to start of day for accurate day counting
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      // Calculate difference in milliseconds and convert to days
      const diffTime = endDate.getTime() - startDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    } catch (error) {
      console.error("Error calculating real days:", error);
      return 0;
    }
  };

  // Calculate business days (Sunday-Thursday only) between two dates
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const calculateBusinessDays = (startDateIso: string, endDateIso: string): number => {
    try {
      const startDate = new Date(startDateIso);
      const endDate = new Date(endDateIso);
      
      // Reset time to start of day for accurate day counting
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      let businessDays = 0;
      const currentDate = new Date(startDate);
      
      // Start from the day after the order (not including order day)
      currentDate.setDate(currentDate.getDate() + 1);
      
      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.getDay();
        // Sunday=0, Monday=1, Tuesday=2, Wednesday=3, Thursday=4
        // Business days are Sunday(0) to Thursday(4)
        if (dayOfWeek >= 0 && dayOfWeek <= 4) {
          businessDays++;
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      return businessDays;
    } catch (error) {
      console.error("Error calculating business days:", error);
      return 0;
    }
  };

  // Format date to DD/MM/YYYY HH:MM AM/PM format
  const formatHebrewDate = (isoString: string): string => {
    try {
      const date = new Date(isoString);

      // Get date components
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      // Get time components
      const hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");

      // Convert to 12-hour format
      const hours12 = hours % 12 || 12;
      const hoursStr = String(hours12).padStart(2, "0");

      // AM/PM
      const period = hours >= 12 ? "PM" : "AM";

      return `${day}/${month}/${year} ${hoursStr}:${minutes} ${period}`;
    } catch (e) {
      return isoString;
    }
  };

  useEffect(() => {
    console.log("TrackingForm component mounted");

    // Get ref parameter from URL
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    const urlTrackingNumber = params.get("trackingNumber");
    console.log("URL ref parameter:", ref);
    console.log("URL trackingNumber parameter:", urlTrackingNumber);

    // Set tracking number from URL if provided
    if (urlTrackingNumber) {
      setTrackingNumber(urlTrackingNumber);
    }

    if (ref) {
      // Fetch store from database
      fetchStoreConfig(ref);
    } else {
      // No ref parameter - show error message
      console.log("No ref parameter provided");
      setStoreLoading(false);
      setError("אנא גש לדף באמצעות קישור חוקי עם פרמטר ref");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-track when store config is loaded and tracking number is in URL
  useEffect(() => {
    if (!storeLoading && trackingNumber && !results && !loading) {
      const params = new URLSearchParams(window.location.search);
      const urlTrackingNumber = params.get("trackingNumber");

      // Only auto-track if the tracking number came from URL
      if (urlTrackingNumber && trackingNumber === urlTrackingNumber) {
        console.log("Auto-tracking from URL parameter:", trackingNumber);
        trackPackage();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeLoading, trackingNumber]);

  const fetchStoreConfig = async (ref: string) => {
    try {
      setStoreLoading(true);
      console.log("Fetching store config for:", ref);
      const response = await fetch(`/api/stores/${ref}`);
      const data = await response.json();
      console.log("Store data received:", data);

      if (data.success && data.data) {
        const storeData = data.data;
        const config: BrandConfig = {
          name: storeData.name,
          logo: storeData.logo,
          colors: storeData.colors,
          text: storeData.text,
        };
        console.log("Applying store config:", config.name);
        setBrandConfig(config);
        applyBrandColors(config);
      } else {
        // Fallback to default if store not found
        console.log("Store not found, using default config");
        const config = getBrandConfig(null);
        setBrandConfig(config);
        applyBrandColors(config);
      }
    } catch (err) {
      console.error("Error fetching store config:", err);
      // Fallback to default
      console.log("Using fallback config due to error");
      const config = getBrandConfig(null);
      setBrandConfig(config);
      applyBrandColors(config);
    } finally {
      setStoreLoading(false);
    }
  };

  const applyBrandColors = (config: BrandConfig) => {
    document.documentElement.style.setProperty("--brand-primary", config.colors.primary);
    document.documentElement.style.setProperty("--brand-background", config.colors.background);
    document.documentElement.style.setProperty("--brand-button-gradient", config.colors.buttonGradient);
    document.documentElement.style.setProperty("--brand-button-hover", config.colors.buttonHover);
    document.documentElement.style.setProperty("--brand-status-background", config.colors.statusBackground);
    document.documentElement.style.setProperty("--brand-status-border", config.colors.statusBorder);
    document.documentElement.style.setProperty("--brand-timeline-dot", config.colors.timelineDot);
    document.documentElement.style.setProperty("--brand-timeline-gradient", config.colors.timelineGradient);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      trackPackage();
    }
  };

  const handleTrackingNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numbers
    const numbersOnly = value.replace(/[^0-9]/g, '');
    setTrackingNumber(numbersOnly);
    
    // Clear error if it was about invalid characters
    if (error && value !== numbersOnly) {
      setError('');
    }
  };

  const trackPackage = async () => {
    if (!trackingNumber.trim()) {
      setError(brandConfig.text.errorEnterNumber);
      return;
    }

    // Validate tracking number length (must be 7-9 digits)
    const trimmedNumber = trackingNumber.trim();
    if (trimmedNumber.length < 7 || trimmedNumber.length > 9) {
      setError("מספר מעקב לא תקין, נא לוודא שמילאתם מספר מעקב ולא מספר הזמנה");
      return;
    }

    setLoading(true);
    setError("");
    setResults(null);

    try {
      // Get store ref from URL
      const params = new URLSearchParams(window.location.search);
      const storeRef = params.get("ref");

      // Track package (backend automatically registers first)
      const response = await fetch("/api/shipping/track", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          trackingNumbers: [trackingNumber.trim()],
          storeRef: storeRef 
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Log received steps from server for translation purposes
        if (data.data?.data?.accepted) {
          data.data.data.accepted.forEach((item: TrackingResult, index: number) => {
            if (item.track_info?.tracking?.providers?.[0]?.events) {
              const steps = item.track_info.tracking.providers[0].events.map((event: TrackingEvent) => event.description);
              console.log(`📦 Received steps for tracking #${index + 1}:`, steps);
              console.log(`📦 Full events data for tracking #${index + 1}:`, item.track_info.tracking.providers[0].events);
            }
          });
        }
        setResults(data.data);
      } else {
        setError(data.message || brandConfig.text.errorTracking);
      }
    } catch (err) {
      setError(brandConfig.text.errorConnection);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const clearAll = () => {
    setTrackingNumber("");
    setResults(null);
    setError("");
  };

  if (storeLoading) {
    return (
      <div className="tracking-container">
        <div className="tracking-form">
          <h1 style={{ textAlign: "center", color: "#333" }}>⏳ טוען...</h1>
        </div>
      </div>
    );
  }

  // If no ref parameter provided, show error
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref");

  if (!ref) {
    return (
      <div className="tracking-container">
        <div className="tracking-form">
          <h1 style={{ textAlign: "center", color: "#333" }}>⚠️ גישה לא חוקית</h1>
          <p style={{ textAlign: "center", color: "#666", fontSize: "18px", marginTop: "20px" }}>
            אנא גש לדף באמצעות קישור חוקי עם פרמטר ref
          </p>
          <p style={{ textAlign: "center", color: "#999", fontSize: "14px", marginTop: "10px" }}>לדוגמה: ?ref=store1</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tracking-container">
      <div className="tracking-form">
        {brandConfig.logo && (
          <div className="brand-logo">
            <img
              src={brandConfig.logo}
              alt={brandConfig.name}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        )}

        <h1>{brandConfig.text.title}</h1>

        <div className="input-section">
          <div className="input-group">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={trackingNumber}
              onChange={handleTrackingNumberChange}
              onKeyPress={handleKeyPress}
              placeholder={brandConfig.text.placeholder}
              className="tracking-input"
              dir="ltr"
            />
            <button onClick={trackPackage} disabled={loading || !trackingNumber.trim()} className="btn btn-track">
              {loading ? brandConfig.text.trackingButton : brandConfig.text.trackButton}
            </button>
          </div>

          {(results || trackingNumber) && (
            <button onClick={clearAll} className="btn-clear">
              {brandConfig.text.clearButton}
            </button>
          )}
        </div>

        {error && <div className="error-message">⚠️ {error}</div>}

        {results && (
          <div className="results-section">
            {/* Accepted/Tracked Package */}
            {results.data && results.data.accepted && results.data.accepted.length > 0 && (
              <div className="tracking-info">
                {results.data.accepted.map((item: TrackingResult, index: number) => {
                  // Get the oldest event (order date) - events are in reverse chronological order
                  const events = item.track_info?.tracking?.providers?.[0]?.events;
                  const oldestEvent = events?.[events.length - 1];
                  
                  // Find the "delivered / closed" event (משלוח התקבל/נסגר)
                  const deliveredEvent = events?.find(event => event.description === "משלוח התקבל/נסגר");
                  
                  // Calculate real days from order to delivery (or to latest event if not delivered)
                  let realDays = 0;
                  if (oldestEvent) {
                    if (deliveredEvent) {
                      // Package delivered: count from order to delivery
                      realDays = calculateRealDays(oldestEvent.time_iso, deliveredEvent.time_iso);
                    } else {
                      // Package not delivered yet: count from order to today
                      const today = new Date().toISOString();
                      realDays = calculateRealDays(oldestEvent.time_iso, today);
                    }
                  }
                  
                  return (
                    <div key={index}>
                      {/* Check if package has no tracking events yet */}
                      {(!events || events.length === 0) ? (
                        <div className="pending-info">
                          <h3>✅ הזמנה אושרה ומספר מעקב הונפק</h3>
                          <p style={{ fontSize: "18px", color: "#2E7D32", fontWeight: "500", marginTop: "15px" }}>
                            נתוני המעקב יתעדכנו אוטומטית ברגע שהפריט ייקלט במערכת המשלוחים (1-2 ימי עסקים).
                          </p>
                        </div>
                      ) : (
                        <>
                          {/* Business Days Section - show for all packages */}
                          {oldestEvent && (
                            <div className="current-status">
                              <strong>ימים שעברו:</strong> {realDays}
                            </div>
                          )}

                          {/* Status Section */}
                          {item.track_info?.latest_status && (
                            <div className="current-status">
                              <strong>{brandConfig.text.statusLabel}</strong> {item.track_info.latest_status.status}
                              {item.track_info.latest_status.sub_status &&
                                item.track_info.latest_status.sub_status !== item.track_info.latest_status.status && (
                                  <span> - {item.track_info.latest_status.sub_status}</span>
                                )}
                            </div>
                          )}

                          {/* Full Tracking History */}
                          {item.track_info?.tracking?.providers?.[0]?.events && (
                            <div className="tracking-history">
                              <h3>{brandConfig.text.historyTitle}</h3>
                              <div className="timeline">
                                {item.track_info.tracking.providers[0].events.map((event: TrackingEvent, idx: number) => (
                                  <div key={idx} className="timeline-item">
                                    <div className="timeline-dot"></div>
                                    <div className="timeline-content">
                                      <div className="event-time">{formatHebrewDate(event.time_iso)}</div>
                                      <div className="event-description">{event.description}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Rejected/Pending Package */}
            {results.data && results.data.rejected && results.data.rejected.length > 0 && (
              <div className="pending-info">
                <h3>{brandConfig.text.pendingTitle}</h3>
                <p>{brandConfig.text.pendingDescription}</p>
                <ul>
                  {brandConfig.text.pendingReasons.map((reason, idx) => (
                    <li key={idx}>{reason}</li>
                  ))}
                </ul>
                <p className="try-again">{brandConfig.text.pendingTryAgain}</p>
                {results.data.rejected.map((item: any, index: number) => (
                  <div key={index} className="error-detail">
                    {item.error?.message || "אין מידע זמין על המעקב"}
                  </div>
                ))}
              </div>
            )}

            {/* No results at all */}
            {(!results.data ||
              ((!results.data.accepted || results.data.accepted.length === 0) &&
                (!results.data.rejected || results.data.rejected.length === 0))) && (
              <div className="no-results">
                <p>{brandConfig.text.noResultsMessage}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackingForm;
